import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { FileSpreadsheet, Plus, RefreshCw, Search } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/shared/components/ui/button';
import { CrudTable, type CrudColumn } from '@/shared/components/ui/crud-table';
import { ErrorDialog, SuccessDialog } from '@/shared/components/ui/status-dialog';
import { Input } from '@/shared/components/ui/input';
import { Switch } from '@/shared/components/ui/switch';
import { useSession, useCanCreateVenta, canActOnVenta } from '@/shared/auth/SessionProvider';
import { formatMoney, parseMoney } from '@/shared/lib/money';
import { downloadXlsx, fechaArchivoHoy } from '@/shared/lib/xlsx';
import {
  listAllOperaciones,
  type ResumenOperacionesItem,
} from '@/shared/api/controllers/resumenOperaciones';
import {
  eliminarVenta,
  listVentasPendingFacturaPercentage,
} from '@/shared/api/controllers/ventas';
import { useActiveCountry } from '@/shared/countries/ActiveCountryProvider';
import type { DetalleNavState } from './detalleNavState';
import { StatusRoBadge } from './components/StatusRoBadge';
import { EstadoConSaldoEcheq, saldoEnDespacho, useSaldosEcheq } from '@/shared/components/EcheqSaldoTag';
import { TipoBadge } from './components/TipoBadge';
import { ConfirmarEliminarDialog } from './components/ConfirmarEliminarDialog';
import { ProcesandoOverlay } from './components/ProcesandoOverlay';
import {
  EMPTY_FILTERS,
  VentasFiltersButton,
  type VentasFilterValue,
} from './components/VentasFilters';
import { inRange, parseAr, parseIso } from './lib/dates';
import { useRealtimeInvalidate } from '@/shared/realtime/useRealtimeInvalidate';

/**
 * Ventas gallery — Stage 1 port of `Screen_Ventas.pa.yaml`.
 *
 * Top toolbar: title + moneda toggle + refresh / export / nueva / search /
 * filtrar. Ventas-role users see only their own rows and lose the
 * Vendedor + F. Creación columns. Totals strip sits pinned at the
 * bottom, matching the PowerApps footer.
 */

const QUERY_KEY = ['ventas', 'resumen-operaciones'] as const;

type Moneda = 'ARS' | 'MXN' | 'USD';

export function VentasPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { profile } = useSession();
  const isVentasRole = profile?.perfil === 'ventas';
  // Nova is an MX-only approver — on this screen they get a preview-only
  // view scoped to Nova-flow rows (FNova_RO === 'SI'). The eye action
  // jumps straight to the Pagos screen (which is itself locked read-only
  // for Nova) and no create/edit/delete affordances render.
  const isNova = profile?.perfil === 'nova';
  const canCreate = useCanCreateVenta();

  // Multi-user gallery — refetch on tab refocus + every 15s while
  // mounted so admins/vendedores see each other's progress without
  // hitting Refrescar. React Query pauses both when the tab is
  // backgrounded.
  // Local currency follows the active country: ARS in AR, MXN in MX.
  // The toggle swaps between the local currency and USD.
  const { config: activeCountry } = useActiveCountry();
  const primaryCurrency = activeCountry.currency.primary as Moneda;
  const countryCode = activeCountry.code;

  const { data: rows = [], isPending, isFetching, error, refetch } = useQuery({
    // Country is part of the key so a fast AR↔MX switch can't serve the
    // previous country's cached page while the new fetch resolves. The
    // module-level QUERY_KEY stays as the invalidation prefix — React
    // Query's partial-prefix match invalidates both countries from one
    // call site.
    queryKey: [...QUERY_KEY, countryCode] as const,
    queryFn: listAllOperaciones,
    staleTime: 60_000,
    // Respaldo cada 2 min: cada vuelta baja TODAS las ventas (~1.6 MB de
    // egress de Supabase); Realtime (abajo) es quien refresca al instante.
    refetchInterval: 120_000,
    refetchOnWindowFocus: true,
  });

  // Live updates: any sales_headers change pushes a refetch instantly.
  // Backstop polling above stays — covers Realtime drops + non-PG paths.
  useRealtimeInvalidate('sales_headers', QUERY_KEY);

  // Factura% pending-approval set — drives the "Pend. Aprob. %F" badge
  // on the Status column. MX-only feature; on AR the endpoint returns
  // an empty array and the badge never appears.
  const factPctPendingQ = useQuery({
    queryKey: ['ventas', 'factura-pct-pending', countryCode],
    queryFn: listVentasPendingFacturaPercentage,
    staleTime: 30_000,
    refetchInterval: 30_000,
    refetchOnWindowFocus: true,
  });
  const factPctPendingSet = useMemo(
    () => new Set(factPctPendingQ.data ?? []),
    [factPctPendingQ.data],
  );
  useRealtimeInvalidate('factura_percentage_authorizations', [
    'ventas',
    'factura-pct-pending',
  ]);

  const [filters, setFilters] = useState<VentasFilterValue>(EMPTY_FILTERS);
  const [search, setSearch] = useState('');
  const [moneda, setMoneda] = useState<Moneda>(primaryCurrency);
  // Reset the moneda when the user switches countries — otherwise the
  // page keeps showing the old country's primary (e.g. lands on MX with
  // moneda='ARS' until the user toggles).
  useEffect(() => {
    setMoneda(primaryCurrency);
  }, [primaryCurrency]);

  // --- delete flow state ---
  const [deleteTarget, setDeleteTarget] = useState<ResumenOperacionesItem | null>(null);
  const [approvalMessage, setApprovalMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const isAdministracion = profile?.perfil === 'administracion';

  const deleteMutation = useMutation({
    mutationFn: async (row: ResumenOperacionesItem) => ({
      response: await eliminarVenta(row.id),
      row,
    }),
    onSuccess: ({ response, row }) => {
      setDeleteTarget(null);
      switch (response.outcome) {
        case 'deleted_cotizacion':
        case 'deleted_venta_direct':
          toast.success(
            response.outcome === 'deleted_cotizacion' ? 'Cotización anulada' : 'Venta anulada',
          );
          queryClient.invalidateQueries({ queryKey: QUERY_KEY });
          // La cascada directa anula las solicitudes de facturación previa
          // pendientes (badge de Aprobaciones) y puede dejar facturas vivas
          // sin venta activa (aviso de Home, key `['facturacion']`).
          queryClient.invalidateQueries({ queryKey: ['aprobaciones'] });
          queryClient.invalidateQueries({ queryKey: ['facturacion'] });
          break;
        case 'pending_approval_created':
          setApprovalMessage(
            `Se envió una solicitud de autorización al administrador para eliminar la venta Nº ${row.fields.NrCotizacion_RO || row.id}.`,
          );
          queryClient.invalidateQueries({ queryKey: QUERY_KEY });
          // Bump the sidebar badge for any admin viewing concurrently.
          queryClient.invalidateQueries({ queryKey: ['aprobaciones'] });
          break;
        case 'blocked_existing_request':
          setApprovalMessage(
            `Ya existe una solicitud pendiente de aprobación sobre la venta Nº ${row.fields.NrCotizacion_RO || row.id}.`,
          );
          break;
        case 'blocked_status':
          setErrorMessage(response.message);
          break;
      }
    },
    onError: (err) => {
      setDeleteTarget(null);
      // eslint-disable-next-line no-console
      console.error('[ventas] eliminarVenta failed', err);
      setErrorMessage(
        'No se pudo eliminar la operación. Intentá nuevamente en unos minutos.',
      );
    },
  });

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const desde = parseIso(filters.desde);
    const hasta = parseIso(filters.hasta);
    const usuarioApp = profile?.usuario_app ?? '';

    // Default order: highest NrCotizacion_RO first (newest quote on top).
    // Fall back to lastModified when two rows share a Nº (or both are blank).
    let out = [...rows].sort((a, b) => {
      const na = Number(a.fields.NrCotizacion_RO);
      const nb = Number(b.fields.NrCotizacion_RO);
      const va = Number.isFinite(na) ? na : -Infinity;
      const vb = Number.isFinite(nb) ? nb : -Infinity;
      if (va !== vb) return vb - va;
      return (b.lastModifiedDateTime ?? '').localeCompare(a.lastModifiedDateTime ?? '');
    });

    if (isVentasRole) {
      out = out.filter((r) => r.fields.Vendedor_RO === usuarioApp);
    }

    if (isNova) {
      // Nova only sees Nova-flow ventas. FNova_RO is server-stamped on save
      // (MXN total above the `Limite Nova` parameter) and is null on AR
      // rows, so this also implicitly scopes Nova to MX even before the
      // país-level filter kicks in.
      out = out.filter((r) => r.fields.FNova_RO === 'SI');
    }

    if (filters.statuses.length > 0) {
      const set = new Set(filters.statuses);
      // The UI collapses `Confirmacion de Pago` into the `Pendiente de Pago`
      // bucket (same badge label/tone, single entry in the filter list),
      // but SharePoint still stores both — so picking the visible label
      // has to match either backing value.
      if (set.has('Pendiente de Pago')) set.add('Confirmacion de Pago');
      out = out.filter((r) => r.fields.Status_RO && set.has(r.fields.Status_RO));
    } else {
      // Anuladas + Cerradas stay hidden unless the user explicitly filters
      // for them — the default view is the live pipeline (everything that
      // still needs attention from a vendor or admin). Both are terminal
      // states whose only purpose is historical lookup.
      out = out.filter(
        (r) =>
          r.fields.Status_RO !== 'Anulado' && r.fields.Status_RO !== 'Cerrado',
      );
    }

    if (desde || hasta) {
      out = out.filter((r) => {
        const d = parseAr(r.fields.FechaPago_RO);
        if (!d) return false;
        return inRange(d, desde, hasta);
      });
    }

    if (q) {
      out = out.filter((r) => {
        const cliente = (r.fields.Cliente_RO ?? '').toLowerCase();
        const correo = (r.fields.Correo_RO ?? '').toLowerCase();
        const vendedor = (r.fields.Vendedor_RO ?? '').toLowerCase();
        const nr = (r.fields.NrCotizacion_RO ?? '').toLowerCase();
        // Match against both the displayed total (cobrable) and the
        // cotización so users can search by either figure — pasting a
        // number from the Pagos screen or from an old quote both work.
        const totalCobrable = String(r.fields.MontoTotalCobro_RO ?? '').toLowerCase();
        const totalCotizacion = String(r.fields.TotalCotizacion_RO ?? '').toLowerCase();
        return (
          cliente.includes(q) ||
          correo.includes(q) ||
          vendedor.includes(q) ||
          nr.includes(q) ||
          totalCobrable.includes(q) ||
          totalCotizacion.includes(q)
        );
      });
    }

    return out;
  }, [rows, filters, search, isVentasRole, isNova, profile?.usuario_app]);

  // Footer breakdown per moneda. Field meanings (from ResumenOperaciones):
  //   - Subtotal_RO          → bruto, sin IVA
  //   - IVA21_RO + IVA10.5_RO → IVA total (worst-case, ignores %factura)
  //   - TotalCotizacion_RO   → bruto + IVA-full (assumes 100% facturado).
  //                            Now used only as a *reference* for the
  //                            tooltip; not displayed as the row total.
  //   - MontoTotalCobro_RO   → what the customer actually pays:
  //                              · subtotal + IVA × (%factura/100), plus
  //                              · payment-method recargo (tarjeta, etc.)
  //                            The displayed Total column anchors to this
  //                            so ventas with %factura<100 stop
  //                            overstating seller revenue (the
  //                            non-facturado portion has no IVA).
  // Three-currency accumulator. Rows are bucketed by `Moneda_RO`; rows
  // missing the field fall into the active country's primary currency
  // (ARS on AR, MXN on MX). Footer below renders only the home + USD
  // buckets, so a stray foreign-currency row would silently drop — but
  // in practice MX ventas land in MXN and AR ventas in ARS.
  //
  // Sums every filtered row (cotizaciones included). Users expect the
  // footer to reflect what's visible on screen — when the previous
  // version skipped cotizaciones the bottom total didn't match the row
  // totals and read as a bug.
  const totals = useMemo(() => {
    const acc = {
      ARS: { bruto: 0, iva: 0, total: 0, cotizacion: 0 },
      MXN: { bruto: 0, iva: 0, total: 0, cotizacion: 0 },
      USD: { bruto: 0, iva: 0, total: 0, cotizacion: 0 },
    };
    for (const r of filtered) {
      const monedaRaw = r.fields.Moneda_RO as Moneda | undefined;
      const m: 'ARS' | 'MXN' | 'USD' =
        monedaRaw === 'USD'
          ? 'USD'
          : monedaRaw === 'MXN'
            ? 'MXN'
            : monedaRaw === 'ARS'
              ? 'ARS'
              : primaryCurrency === 'MXN'
                ? 'MXN'
                : 'ARS';
      // `cotizacion` is the bruto+IVA-full reference (assumes 100%
      // facturado); `total` is what the customer actually pays — drops
      // the IVA on the non-facturado portion when %factura<100, and
      // adds payment-method recargo on top. Footer's Total column
      // anchors to `total`; the tooltip shows `cotizacion` for
      // comparison when they differ.
      //
      // Note `MontoTotalCobro_RO` is initialised to `0` (not null) for
      // cotizaciones that haven't been to Pagos yet, so `?? cotizacion`
      // isn't enough — that would surface `0` instead of the
      // cotización amount. Treat `cobrable<=0` as "no Pagos breakdown
      // yet" and fall back to the cotización; non-zero (even if it's
      // less than cotización because of %factura<100) wins.
      const cotizacion = parseMoney(r.fields.TotalCotizacion_RO) ?? 0;
      const cobrable = parseMoney(r.fields.MontoTotalCobro_RO) ?? 0;
      const total = cobrable > 0 ? cobrable : cotizacion;
      const iva21 = parseMoney(r.fields.IVA21_RO) ?? 0;
      const iva105 = parseMoney(r.fields.IVA10_x002e_5_RO) ?? 0;
      const ivaSplit = iva21 + iva105;
      // Subtotal fallback anchors to `cotizacion` (bruto + IVA-full), not
      // `total` — otherwise a %factura<100 row's `cotizacion − ivaSplit`
      // would under-state the bruto when Subtotal_RO is missing.
      const subtotal = parseMoney(r.fields.Subtotal_RO) ?? Math.max(0, cotizacion - ivaSplit);
      // Prefer the explicit IVA split; fall back to (cotizacion − subtotal)
      // when neither IVA column was populated.
      const iva = ivaSplit > 0 ? ivaSplit : Math.max(0, cotizacion - subtotal);
      acc[m].bruto += subtotal;
      acc[m].iva += iva;
      acc[m].total += total;
      acc[m].cotizacion += cotizacion;
    }
    return acc;
  }, [filtered, primaryCurrency]);

  const saldosEcheq = useSaldosEcheq();
  const columns: CrudColumn<ResumenOperacionesItem>[] = useMemo(() => {
    const base: CrudColumn<ResumenOperacionesItem>[] = [
      {
        header: 'Tipo',
        width: 'w-24',
        render: (r) => <TipoBadge tipo={r.fields.Tipo_RO} />,
      },
      {
        header: 'Status',
        width: 'w-36',
        render: (r) => (
          <EstadoConSaldoEcheq saldo={saldoEnDespacho(saldosEcheq, r.id, r.fields.Status_RO)}>
            <StatusRoBadge status={r.fields.Status_RO} />
          </EstadoConSaldoEcheq>
        ),
      },
      {
        header: 'Nº',
        width: 'w-16',
        render: (r) => (
          <span className="font-semibold text-foreground">{r.fields.NrCotizacion_RO || '—'}</span>
        ),
      },
    ];

    if (!isVentasRole) {
      base.push({
        header: 'F. Creación',
        width: 'w-24',
        render: (r) => (
          <span className="whitespace-nowrap text-muted-foreground">
            {r.fields.Fecha_RO || '—'}
          </span>
        ),
      });
    }

    base.push({
      header: 'F. Venta',
      width: 'w-24',
      render: (r) => (
        <span className="whitespace-nowrap text-muted-foreground">
          {r.fields.FechaPago_RO || '—'}
        </span>
      ),
    });

    if (!isVentasRole) {
      base.push({
        header: 'Vendedor',
        width: 'w-24',
        render: (r) => (
          <span className="whitespace-nowrap text-muted-foreground">
            {r.fields.Vendedor_RO || '—'}
          </span>
        ),
      });
    }

    base.push(
      {
        header: 'Cliente',
        render: (r) => (
          <div className="flex flex-col leading-tight">
            <span className="font-medium text-foreground">{r.fields.Cliente_RO || '—'}</span>
            {r.fields.Correo_RO ? (
              <span className="break-all text-xs text-muted-foreground">
                {r.fields.Correo_RO}
              </span>
            ) : null}
          </div>
        ),
      },
      {
        header: 'Total',
        width: 'w-36',
        align: 'right',
        render: (r) => {
          const rowMoneda = (r.fields.Moneda_RO as Moneda) ?? primaryCurrency;
          const spId = Number(r.id);
          const isPendingFactPct =
            Number.isFinite(spId) && factPctPendingSet.has(spId);
          const isUsado = r.fields.FacturaInternaUsado_RO === true;
          // Render `MontoTotalCobro_RO` (what the customer actually
          // pays — drops non-facturado IVA, adds recargo). Falls back
          // to `TotalCotizacion_RO` for legacy rows that never populated
          // the cobro column AND for freshly-created cotizaciones where
          // the cobro is stored as `0` (not null) until Pagos runs.
          // Treat `cobrable<=0` as "no breakdown yet"; non-zero wins
          // even when it's less than cotización (the %factura<100 case).
          const cobrableValue = parseMoney(r.fields.MontoTotalCobro_RO);
          const rowTotal =
            cobrableValue != null && cobrableValue > 0
              ? r.fields.MontoTotalCobro_RO
              : r.fields.TotalCotizacion_RO;
          return (
            <div className="flex flex-col items-end gap-1">
              <span className="whitespace-nowrap font-semibold text-foreground">
                {formatMoney(rowTotal, rowMoneda)}{' '}
                <span className="text-xs font-medium text-muted-foreground">{rowMoneda}</span>
              </span>
              {isUsado && (
                <span
                  className="inline-flex items-center rounded-full border border-rose-300 bg-rose-50 px-2 py-0.5 text-[10px] font-semibold uppercase leading-tight tracking-wide text-rose-700"
                  title="Esta venta queda por debajo del costo contable + comisión + IVA — la factura interna saldrá como usado."
                >
                  Usado
                </span>
              )}
              {isPendingFactPct && (
                <span
                  className="inline-flex items-center rounded-full border border-amber-300 bg-amber-50 px-2 py-0.5 text-[10px] font-semibold uppercase leading-tight tracking-wide text-amber-800"
                  title="Pendiente de aprobación del porcentaje de facturación"
                >
                  Pend. aprob. %F
                </span>
              )}
            </div>
          );
        },
      },
    );

    return base;
  }, [isVentasRole, primaryCurrency, factPctPendingSet, saldosEcheq]);

  const handleRefresh = () => {
    queryClient.invalidateQueries({ queryKey: QUERY_KEY });
  };

  return (
    <div className="flex h-full flex-col gap-4">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-3">
        <h1 className="mr-auto text-3xl font-semibold tracking-tight text-foreground">Ventas</h1>

        {/* Moneda toggle — compact pill on mobile, switch on desktop.
            Swaps between the active country's local currency
            (ARS / MXN) and USD. */}
        <button
          type="button"
          onClick={() => setMoneda(moneda === 'USD' ? primaryCurrency : 'USD')}
          className="inline-flex h-10 w-12 shrink-0 items-center justify-center rounded-lg border bg-background text-xs font-semibold text-foreground transition-colors hover:bg-secondary sm:hidden"
          aria-label={`Moneda actual: ${moneda}. Tocá para cambiar.`}
          title={`Moneda actual: ${moneda} — tocá para cambiar`}
        >
          {moneda}
        </button>
        <div className="hidden h-12 shrink-0 items-center gap-2 rounded-lg bg-secondary px-3 sm:flex">
          <Switch
            checked={moneda === 'USD'}
            onCheckedChange={(v) => setMoneda(v ? 'USD' : primaryCurrency)}
            aria-label="Cambiar moneda utilizada"
          />
          <span className="text-sm font-medium text-foreground">
            Moneda Utilizada: <span className="font-semibold">{moneda}</span>
          </span>
        </div>

        {canCreate && (
          <Button
            className="h-12 w-12 shrink-0 gap-2 bg-foreground p-0 text-background hover:bg-foreground/90 sm:w-auto sm:px-4"
            onClick={() => navigate(`/ventas/nueva?moneda=${moneda}`)}
            aria-label="Nueva cotización"
            title="Nueva cotización"
          >
            <span className="hidden sm:inline">Nueva cotización</span>
            <Plus className="h-4 w-4" aria-hidden />
          </Button>
        )}

        <Button
          variant="outline"
          size="icon"
          className="h-12 w-12 shrink-0"
          aria-label="Exportar a Excel"
          title="Exportar a Excel"
          onClick={() => void exportVentasToXlsx(filtered, isVentasRole)}
          disabled={filtered.length === 0}
        >
          <FileSpreadsheet className="h-4 w-4" aria-hidden />
        </Button>

        <Button
          variant="outline"
          size="icon"
          className="h-12 w-12 shrink-0"
          onClick={handleRefresh}
          aria-label="Actualizar"
          title="Actualizar"
        >
          <RefreshCw className={isFetching ? 'h-4 w-4 animate-spin' : 'h-4 w-4'} aria-hidden />
        </Button>

        <div className="relative min-w-[10rem] flex-1 basis-48">
          <Search
            className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
            aria-hidden
          />
          <Input
            variant="filled"
            placeholder="Búsqueda"
            className="pl-9"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            aria-label="Buscar"
          />
        </div>

        <VentasFiltersButton value={filters} onChange={setFilters} />
      </div>

      {/* Table */}
      <div className="min-h-0 flex-1">
        <CrudTable
          rows={filtered}
          columns={columns}
          loading={isPending}
          error={error instanceof Error ? error : null}
          onRetry={() => refetch()}
          emptyMessage={
            search ? `No hay resultados para "${search}".` : 'No hay operaciones cargadas.'
          }
          onView={(r) =>
            isNova
              ? // Nova has no access to the editable detail page — jump
                // straight to the Pagos screen. The page picks up
                // `isNova` from the session and renders read-only; the
                // explicit `from: 'ventas'` flag tells it to send the
                // back button to /ventas (not the default /ventas/:id,
                // which Nova also can't enter).
                navigate(`/ventas/${r.id}/pagos`, {
                  state: { from: 'ventas' as const },
                })
              : navigate(`/ventas/${r.id}`, { state: { header: r } satisfies DetalleNavState })
          }
          onDelete={isAdministracion || isNova ? undefined : (r) => setDeleteTarget(r)}
          // Marketing sees all ventas but may only delete its OWN; every other
          // role keeps its access (canActOnVenta == !isReadOnly for them).
          canRowWrite={(r) => canActOnVenta(profile, r.fields.Vendedor_RO ?? null)}
          canDelete={(r) => isRowDeletable(r.fields.Status_RO)}
          deleteDisabledTitle={(r) => {
            const status = r.fields.Status_RO ?? '';
            if (status === 'Anulado') return 'La operación ya está anulada';
            if (NON_DELETABLE_STATUSES.includes(status)) {
              return `No se puede eliminar una operación en estado "${status}"`;
            }
            return undefined;
          }}
          isBackgroundFetching={isFetching}
        />
      </div>

      {/* Totals footer — Bruto / IVA / Total per moneda. Sums every
          filtered row so the bottom totals always match what's visible
          on screen. The home-currency block follows the active country
          (Pesos on AR, Pesos Mexicanos on MX); the USD block is always
          shown. */}
      <div className="flex shrink-0 flex-wrap items-center justify-between gap-3 rounded-xl border bg-card px-3 py-3 text-sm sm:justify-end sm:gap-8 sm:px-5">
        {primaryCurrency === 'MXN' ? (
          <TotalsBlock label="Pesos Mexicanos" moneda="MXN" totals={totals.MXN} />
        ) : (
          <TotalsBlock label="Pesos" moneda="ARS" totals={totals.ARS} />
        )}
        <TotalsBlock label="Dólares" moneda="USD" totals={totals.USD} />
      </div>

      <ConfirmarEliminarDialog
        open={deleteTarget !== null && !deleteMutation.isPending}
        tipo={deleteTarget?.fields.Tipo_RO === 'Cotizacion' ? 'Cotizacion' : 'Venta'}
        nr={deleteTarget?.fields.NrCotizacion_RO || deleteTarget?.id || ''}
        cliente={deleteTarget?.fields.Cliente_RO?.trim() || '—'}
        operacionId={deleteTarget?.id ?? null}
        onCancel={() => setDeleteTarget(null)}
        onConfirm={() => deleteTarget && deleteMutation.mutate(deleteTarget)}
      />
      <SuccessDialog
        open={approvalMessage !== null}
        title="Solicitud enviada"
        message={approvalMessage ?? ''}
        onClose={() => setApprovalMessage(null)}
      />
      <ErrorDialog
        open={errorMessage !== null}
        message={errorMessage ?? ''}
        onClose={() => setErrorMessage(null)}
      />
      {deleteMutation.isPending && <ProcesandoOverlay message="Eliminando…" />}
    </div>
  );
}

const NON_DELETABLE_STATUSES = [
  'Cerrado',
  'Pendiente Puesta Marcha',
  'Pendiente Despacho',
  'Anulado',
];

function isRowDeletable(status: string | null | undefined): boolean {
  if (!status) return true;
  return !NON_DELETABLE_STATUSES.includes(status);
}

function TotalsBlock({
  label,
  moneda,
  totals,
}: {
  label: string;
  moneda: Moneda;
  totals: { bruto: number; iva: number; total: number; cotizacion: number };
}) {
  const empty = totals.total === 0 && totals.bruto === 0 && totals.iva === 0;
  const fmt = (n: number) => (n > 0 ? formatMoney(n, moneda) : '—');
  // Two directions of divergence between `total` (lo que paga el
  // cliente) and `cotizacion` (bruto + IVA al 100% facturado):
  //   · `total > cotizacion` → recargo financiero (tarjeta en cuotas).
  //   · `total < cotizacion` → %factura<100: el cliente paga subtotal +
  //     IVA solo sobre la parte facturada, el resto va en efectivo
  //     sin IVA. La diferencia es IVA que no se cobra.
  // Tooltip surfaces whichever side es non-zero so el operador entiende
  // por qué la columna Total no es `bruto + IVA-completo`.
  const recargo = Math.max(0, totals.total - totals.cotizacion);
  const ivaNoFacturado = Math.max(0, totals.cotizacion - totals.total);
  let totalTooltip: string;
  if (recargo > 0) {
    totalTooltip = `Cotización (bruto + IVA): ${fmt(totals.cotizacion)}\nRecargo por medio de pago: ${fmt(recargo)}\nTotal a cobrar: ${fmt(totals.total)}`;
  } else if (ivaNoFacturado > 0) {
    totalTooltip = `Cotización si fuera 100% facturado: ${fmt(totals.cotizacion)}\nIVA no facturado (cobro en efectivo): ${fmt(ivaNoFacturado)}\nTotal a cobrar: ${fmt(totals.total)}`;
  } else {
    totalTooltip = 'Total a cobrar. Sin recargo y 100% facturado — coincide con la suma de cada venta visible.';
  }
  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm">
      <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
        {label}
      </span>
      {empty ? (
        <span className="text-muted-foreground">—</span>
      ) : (
        <>
          <span className="text-muted-foreground">
            Bruto:{' '}
            <span className="font-medium text-foreground">{fmt(totals.bruto)}</span>
          </span>
          <span className="text-muted-foreground">
            IVA:{' '}
            <span className="font-medium text-foreground">{fmt(totals.iva)}</span>
          </span>
          <span
            className="cursor-help text-muted-foreground underline decoration-dotted underline-offset-4"
            title={totalTooltip}
          >
            Total:{' '}
            <span className="font-semibold text-foreground">{fmt(totals.total)}</span>
          </span>
        </>
      )}
    </div>
  );
}

/**
 * Build an XLSX from the current filtered Ventas view and trigger a
 * browser download. Column set follows what's visible in the gallery
 * and respects the Ventas-role hide of Vendedor + F. Creación.
 */
async function exportVentasToXlsx(
  rows: ResumenOperacionesItem[],
  isVentasRole: boolean,
): Promise<void> {
  const headers = ['Tipo', 'Status', 'Nº'];
  if (!isVentasRole) headers.push('F. Creación');
  headers.push('F. Venta');
  if (!isVentasRole) headers.push('Vendedor');
  headers.push('Cliente', 'Correo', 'Total', 'Moneda');

  const data = rows.map((r) => {
    const f = r.fields;
    const moneda = (f.Moneda_RO as Moneda) ?? 'ARS';
    const cells: Array<string | number | null | undefined> = [
      f.Tipo_RO ?? '',
      f.Status_RO ?? '',
      f.NrCotizacion_RO ?? '',
    ];
    if (!isVentasRole) cells.push(f.Fecha_RO ?? '');
    cells.push(f.FechaPago_RO ?? '');
    if (!isVentasRole) cells.push(f.Vendedor_RO ?? '');
    // Match the on-screen Total column: actually-cobrable amount with
    // cotización fallback. Keeps the XLSX export aligned with what
    // operators see in the gallery (matters for any row with
    // %factura<100). `?? 0` isn't enough — fresh cotizaciones land
    // here with MontoTotalCobro=0, not null, so we'd export $0; treat
    // cobrable<=0 as "fall back to cotización".
    const cobrableForXlsx = parseMoney(f.MontoTotalCobro_RO);
    const totalCell =
      cobrableForXlsx != null && cobrableForXlsx > 0
        ? f.MontoTotalCobro_RO ?? ''
        : f.TotalCotizacion_RO ?? '';
    cells.push(
      f.Cliente_RO ?? '',
      f.Correo_RO ?? '',
      totalCell,
      moneda,
    );
    return cells;
  });

  const today = fechaArchivoHoy();
  await downloadXlsx({
    filename: `ventas_${today}.xlsx`,
    sheetName: 'Ventas',
    headers,
    rows: data,
  });
}
